/**
 * Time Entries — review and report on actual logged work intervals.
 *
 * This page is intentionally about actual records in `time_entries`: manual
 * entries, Work Timer sessions, Clockify CSV rows, completed Google Tasks, and
 * legacy calendar-import rows that predate the Planned Schedule split. New
 * Calendar ICS and Google Calendar imports live in Planned Schedule.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import StopIcon from '@mui/icons-material/Stop';
import TimerIcon from '@mui/icons-material/Timer';
import axios from 'axios';

import BackfillWizard, { BackfillGap } from '../components/BackfillWizard';
import CollapsibleFilterBar from '../components/CollapsibleFilterBar';
import OverlapConflictDialog, {
  OverlapEntrySummary,
} from '../components/OverlapConflictDialog';
import PageShell from '../components/PageShell';
import { useLayoutTokens } from '../hooks/useLayoutTokens';
import { getApiErrorMessage } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const PAGE_SIZE = 200;

type TimeEntrySource =
  | 'manual'
  | 'clockify_csv'
  | 'calendar_ics'
  | 'google_calendar'
  | 'google_tasks';

interface TimeEntry {
  id: number;
  user_id: number;
  work_timer_id?: number | null;
  project_id?: number | null;
  description?: string | null;
  project?: string | null;
  client?: string | null;
  task?: string | null;
  tags?: string | null;
  source_user_name?: string | null;
  source_user_group?: string | null;
  source_user_email?: string | null;
  billable?: boolean | null;
  billable_rate?: number | null;
  billable_rate_currency?: string | null;
  billable_amount?: number | null;
  billable_amount_currency?: string | null;
  start_time: string;
  end_time?: string | null;
  expected_end?: string | null;
  duration_seconds?: number | null;
  duration_decimal?: number | null;
  source_created_at?: string | null;
  source: TimeEntrySource;
  external_id?: string | null;
  created_at: string;
  updated_at?: string | null;
  // Present on create/update/stop responses: closed entries this write
  // overlaps (warn-only; overlapping entries are first-class behaviour).
  overlaps?: OverlapEntrySummary[];
}

// One row from GET /api/time-entries/overlaps (a.id < b.id, each pair once).
interface OverlapPair {
  entry_a: OverlapEntrySummary;
  entry_b: OverlapEntrySummary;
  acknowledged: boolean;
}

interface WorkTimer {
  id: number;
  name: string;
  project?: string | null;
  color: string;
}

interface BreakdownItem {
  key?: string | null;
  entry_count: number;
  duration_seconds: number;
  billable_duration_seconds: number;
  billable_amount?: number | null;
  billable_amount_currency?: string | null;
}

interface TimeEntrySummary {
  total_duration_seconds: number;
  billable_duration_seconds: number;
  billable_amount?: number | null;
  billable_amount_currency?: string | null;
  entry_count: number;
  breakdowns: {
    source: BreakdownItem[];
    project: BreakdownItem[];
    client: BreakdownItem[];
    task: BreakdownItem[];
  };
}

interface Filters {
  startDate: string;
  endDate: string;
  source: 'all' | TimeEntrySource;
  project: string;
  client: string;
  task: string;
  workTimerId: string;
  billable: 'all' | 'true' | 'false';
  runningOnly: boolean;
  q: string;
}

interface EntryFormState {
  work_timer_id: string;
  description: string;
  project: string;
  client: string;
  task: string;
  tags: string;
  billable: 'unset' | 'true' | 'false';
  billable_rate: string;
  billable_rate_currency: string;
  billable_amount: string;
  billable_amount_currency: string;
  start_time: string;
  end_time: string;
  expected_end: string;
  duration_decimal: string;
  source_created_at: string;
  source_user_name: string;
  source_user_group: string;
  source_user_email: string;
}

// Flexible time-capture quick-entry surface. All three modes create ordinary
// `time_entries` rows (source=manual on the server); only the timestamps differ.
//   worked   → completed entry, end_time = anchor, start_time = end - duration
//   started  → running entry (end_time null) from a chosen start time
//   continue → running entry + expected_end (a soft "plan to stop" target)
type QuickMode = 'worked' | 'started' | 'continue';

interface QuickLogState {
  mode: QuickMode;
  description: string;
  project: string;
  work_timer_id: string;
  duration_minutes: string; // worked mode — effective minutes
  worked_end: string; // worked mode — anchor end (datetime-local), default now
  started_at: string; // started / continue — datetime-local, default now
  continue_minutes: string; // continue mode — "plan N more" minutes
}

// Quick duration presets (minutes). Also used for "plan to continue".
const DURATION_CHIPS = [15, 20, 30, 45, 60, 90];
// Sanity cap for quick logging: reject absurd durations (24h). Historical
// entries with an explicit start+end are unaffected — this only guards the
// duration/plan-to-continue inputs on the quick surface.
const MAX_LOG_MINUTES = 24 * 60;
// Grace window so trivial clock skew doesn't reject a "now" start/end.
const FUTURE_TOLERANCE_MS = 60 * 1000;

const SOURCE_LABELS: Record<TimeEntrySource, string> = {
  manual: 'Manual',
  clockify_csv: 'Clockify CSV',
  calendar_ics: 'Calendar ICS (legacy)',
  google_calendar: 'Google Calendar (legacy)',
  google_tasks: 'Google Tasks',
};

const SOURCE_OPTIONS: TimeEntrySource[] = [
  'manual',
  'clockify_csv',
  'calendar_ics',
  'google_calendar',
  'google_tasks',
];

const emptyFilters: Filters = {
  startDate: '',
  endDate: '',
  source: 'all',
  project: '',
  client: '',
  task: '',
  workTimerId: '',
  billable: 'all',
  runningOnly: false,
  q: '',
};

const toLocalInputValue = (date: Date): string => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const toLocalDateValue = (date: Date): string => toLocalInputValue(date).slice(0, 10);

const isoToLocalInputValue = (iso?: string | null): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return toLocalInputValue(date);
};

const createDefaultForm = (): EntryFormState => {
  const end = new Date();
  const start = new Date(end.getTime() - 60 * 60 * 1000);
  return {
    work_timer_id: '',
    description: '',
    project: '',
    client: '',
    task: '',
    tags: '',
    billable: 'unset',
    billable_rate: '',
    billable_rate_currency: '',
    billable_amount: '',
    billable_amount_currency: '',
    start_time: toLocalInputValue(start),
    end_time: toLocalInputValue(end),
    expected_end: '',
    duration_decimal: '',
    source_created_at: '',
    source_user_name: '',
    source_user_group: '',
    source_user_email: '',
  };
};

const createDefaultQuick = (): QuickLogState => {
  const now = toLocalInputValue(new Date());
  return {
    mode: 'worked',
    description: '',
    project: '',
    work_timer_id: '',
    duration_minutes: '30',
    worked_end: now,
    started_at: now,
    continue_minutes: '20',
  };
};

const formFromEntry = (entry: TimeEntry): EntryFormState => ({
  work_timer_id: entry.work_timer_id == null ? '' : String(entry.work_timer_id),
  description: entry.description || '',
  project: entry.project || '',
  client: entry.client || '',
  task: entry.task || '',
  tags: entry.tags || '',
  billable:
    entry.billable == null ? 'unset' : entry.billable ? 'true' : 'false',
  billable_rate: entry.billable_rate == null ? '' : String(entry.billable_rate),
  billable_rate_currency: entry.billable_rate_currency || '',
  billable_amount: entry.billable_amount == null ? '' : String(entry.billable_amount),
  billable_amount_currency: entry.billable_amount_currency || '',
  start_time: isoToLocalInputValue(entry.start_time),
  end_time: isoToLocalInputValue(entry.end_time),
  expected_end: isoToLocalInputValue(entry.expected_end),
  duration_decimal: entry.duration_decimal == null ? '' : String(entry.duration_decimal),
  source_created_at: isoToLocalInputValue(entry.source_created_at),
  source_user_name: entry.source_user_name || '',
  source_user_group: entry.source_user_group || '',
  source_user_email: entry.source_user_email || '',
});

const optionalString = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed || null;
};

const optionalNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
};

const toIsoFromDate = (date: string, endOfDay: boolean): string | null => {
  if (!date) return null;
  const suffix = endOfDay ? 'T23:59:59.999' : 'T00:00:00.000';
  const parsed = new Date(`${date}${suffix}`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
};

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const formatDuration = (seconds?: number | null): string => {
  if (seconds == null) return 'Running';
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const h = Math.floor(safeSeconds / 3600);
  const m = Math.floor((safeSeconds % 3600) / 60);
  const s = safeSeconds % 60;
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${s.toString().padStart(2, '0')}s`;
  return `${s}s`;
};

// Validate a quick-log minutes input: positive and within the 24h cap.
// Throws a user-facing Error (surfaced the same way as buildPayload).
const parseQuickMinutes = (value: string, label: string): number => {
  const trimmed = value.trim();
  const parsed = Number(trimmed);
  if (!trimmed || !Number.isFinite(parsed) || parsed <= 0) {
    throw new Error(`${label} must be a positive number of minutes`);
  }
  if (parsed > MAX_LOG_MINUTES) {
    throw new Error(`${label} can't exceed 24 hours`);
  }
  return parsed;
};

// For a running entry with an expected_end target, describe the soft target and
// remaining/over indicator relative to `now`. Returns null for non-running rows
// or rows without a target. Never drives any auto-stop — display only.
const describeExpectedEnd = (
  entry: TimeEntry,
  now: number,
): { over: boolean; text: string } | null => {
  if (entry.end_time != null || !entry.expected_end) return null;
  const target = new Date(entry.expected_end).getTime();
  if (Number.isNaN(target)) return null;
  const diffMinutes = Math.round((target - now) / 60000);
  const over = diffMinutes < 0;
  const magnitude = Math.abs(diffMinutes);
  const relative = over ? `over by ${magnitude}m` : `${magnitude}m left`;
  const clock = new Date(target).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
  return { over, text: `Plan to stop ${clock} · ${relative}` };
};

const formatMoney = (amount?: number | null, currency?: string | null): string => {
  if (amount == null) return 'Mixed';
  const code = currency || 'USD';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)}${currency ? ` ${currency}` : ''}`;
  }
};

const triggerBlobDownload = (data: BlobPart, contentType: string, filename: string): void => {
  const blob = new Blob([data], { type: contentType || 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const TimeEntries: React.FC = () => {
  const {
    cardContentPaddingSx,
    dialogContentPaddingSx,
    pageGridSpacing,
    pageHeaderSx,
    panelPaddingSx,
    sectionGapSx,
    tableCellPaddingSx,
  } = useLayoutTokens();
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [summary, setSummary] = useState<TimeEntrySummary | null>(null);
  const [workTimers, setWorkTimers] = useState<WorkTimer[]>([]);
  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [form, setForm] = useState<EntryFormState>(createDefaultForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TimeEntry | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [quickOpen, setQuickOpen] = useState(false);
  const [quick, setQuick] = useState<QuickLogState>(createDefaultQuick);
  const [quickSaving, setQuickSaving] = useState(false);
  const [stoppingId, setStoppingId] = useState<number | null>(null);

  // Backfill wizard: opened from the header, or via the dismissible
  // auto-offer that appears when a save leaves an unaccounted gap behind it.
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardGap, setWizardGap] = useState<BackfillGap | null>(null);
  const [offerGap, setOfferGap] = useState<BackfillGap | null>(null);
  // Overlap handling: the non-blocking conflict dialog (fed by save/stop
  // responses carrying unacknowledged overlaps) and the review panel
  // (GET /overlaps — the audit surface for never-checked historical imports).
  const [conflictEntry, setConflictEntry] = useState<TimeEntry | null>(null);
  const [conflictOverlaps, setConflictOverlaps] = useState<OverlapEntrySummary[]>([]);
  // Conflicted rows still waiting their turn in the single-entry dialog. Read
  // only inside its own updater, which shifts the next row into the dialog.
  const [, setConflictQueue] = useState<
    { entry: TimeEntry; overlaps: OverlapEntrySummary[] }[]
  >([]);
  const [overlapPanelOpen, setOverlapPanelOpen] = useState(false);
  const [overlapPairs, setOverlapPairs] = useState<OverlapPair[]>([]);
  const [overlapLoading, setOverlapLoading] = useState(false);
  const [ackBusyKey, setAckBusyKey] = useState<string | null>(null);
  // Coarse clock tick so the remaining/over indicator on running entries with
  // an expected_end target stays roughly current without a per-second timer.
  const [nowTick, setNowTick] = useState<number>(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNowTick(Date.now()), 60000);
    return () => clearInterval(interval);
  }, []);

  const hasRunningEntry = useMemo(
    () => entries.some((entry) => entry.end_time == null),
    [entries],
  );

  const timerById = useMemo(() => {
    const map = new Map<number, WorkTimer>();
    for (const timer of workTimers) map.set(timer.id, timer);
    return map;
  }, [workTimers]);

  const activeFilterCount = useMemo(
    () =>
      [
        Boolean(filters.startDate),
        Boolean(filters.endDate),
        filters.source !== 'all',
        Boolean(filters.project.trim()),
        Boolean(filters.client.trim()),
        Boolean(filters.task.trim()),
        Boolean(filters.workTimerId),
        filters.billable !== 'all',
        filters.runningOnly,
        Boolean(filters.q.trim()),
      ].filter(Boolean).length,
    [filters],
  );

  const filterParams = useMemo(() => {
    const params: Record<string, string | number | boolean> = {};
    const from = toIsoFromDate(filters.startDate, false);
    const to = toIsoFromDate(filters.endDate, true);
    if (from) params.from = from;
    if (to) params.to = to;
    if (filters.source !== 'all') params.source = filters.source;
    if (filters.project.trim()) params.project = filters.project.trim();
    if (filters.client.trim()) params.client = filters.client.trim();
    if (filters.task.trim()) params.task = filters.task.trim();
    if (filters.workTimerId) params.work_timer_id = Number(filters.workTimerId);
    if (filters.billable !== 'all') params.billable = filters.billable === 'true';
    if (filters.runningOnly) params.running_only = true;
    if (filters.q.trim()) params.q = filters.q.trim();
    return params;
  }, [filters]);

  const fetchWorkTimers = useCallback(async () => {
    try {
      const response = await axios.get<WorkTimer[]>(`${API_BASE_URL}/api/work-timers/`);
      setWorkTimers(response.data);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load work timers'));
    }
  }, []);

  const fetchInitial = useCallback(async () => {
    try {
      setLoading(true);
      const [entriesRes, summaryRes] = await Promise.all([
        axios.get<TimeEntry[]>(`${API_BASE_URL}/api/time-entries/`, {
          params: { ...filterParams, limit: PAGE_SIZE, offset: 0 },
        }),
        axios.get<TimeEntrySummary>(`${API_BASE_URL}/api/time-entries/summary`, {
          params: filterParams,
        }),
      ]);
      setEntries(entriesRes.data);
      setSummary(summaryRes.data);
      setError(null);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load time entries'));
    } finally {
      setLoading(false);
    }
  }, [filterParams]);

  useEffect(() => {
    fetchWorkTimers();
  }, [fetchWorkTimers]);

  useEffect(() => {
    fetchInitial();
  }, [fetchInitial]);

  const loadMore = async () => {
    try {
      setLoadingMore(true);
      const response = await axios.get<TimeEntry[]>(`${API_BASE_URL}/api/time-entries/`, {
        params: { ...filterParams, limit: PAGE_SIZE, offset: entries.length },
      });
      setEntries((prev) => [...prev, ...response.data]);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load more time entries'));
    } finally {
      setLoadingMore(false);
    }
  };

  const setFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const openCreate = () => {
    setEditingEntry(null);
    setForm(createDefaultForm());
    setDialogOpen(true);
  };

  const openEdit = (entry: TimeEntry) => {
    if (entry.source !== 'manual') return;
    setEditingEntry(entry);
    setForm(formFromEntry(entry));
    setDialogOpen(true);
  };

  const buildPayload = () => {
    if (!form.start_time) {
      throw new Error('Start time is required');
    }
    if (!editingEntry && !form.end_time) {
      throw new Error('End time is required for manual entries');
    }

    const billableRate = optionalNumber(form.billable_rate);
    const billableAmount = optionalNumber(form.billable_amount);
    const durationDecimal = optionalNumber(form.duration_decimal);
    if (Number.isNaN(billableRate) || Number.isNaN(billableAmount) || Number.isNaN(durationDecimal)) {
      throw new Error('Numeric fields must contain valid numbers');
    }

    return {
      work_timer_id: form.work_timer_id ? Number(form.work_timer_id) : null,
      description: optionalString(form.description),
      project: optionalString(form.project),
      client: optionalString(form.client),
      task: optionalString(form.task),
      tags: optionalString(form.tags),
      source_user_name: optionalString(form.source_user_name),
      source_user_group: optionalString(form.source_user_group),
      source_user_email: optionalString(form.source_user_email),
      billable:
        form.billable === 'unset' ? null : form.billable === 'true',
      billable_rate: billableRate,
      billable_rate_currency: optionalString(form.billable_rate_currency)?.toUpperCase() || null,
      billable_amount: billableAmount,
      billable_amount_currency: optionalString(form.billable_amount_currency)?.toUpperCase() || null,
      start_time: new Date(form.start_time).toISOString(),
      end_time: form.end_time ? new Date(form.end_time).toISOString() : null,
      expected_end: form.expected_end ? new Date(form.expected_end).toISOString() : null,
      duration_decimal: durationDecimal,
      source_created_at: form.source_created_at
        ? new Date(form.source_created_at).toISOString()
        : null,
    };
  };

  // When save/stop responses carry unacknowledged overlaps, open the
  // non-blocking conflict dialog. Acknowledged pairs never re-warn — the
  // owner already answered for them.
  //
  // A backfill batch can land several conflicted rows at once, and the dialog
  // is built around a single entry, so extras queue up behind the first and
  // are presented one at a time as each is settled.
  const queueOverlapWarnings = useCallback((rows: (TimeEntry | null | undefined)[]) => {
    const pending = rows
      .filter((row): row is TimeEntry => Boolean(row))
      .map((row) => ({
        entry: row,
        overlaps: Array.isArray(row.overlaps)
          ? (row.overlaps as OverlapEntrySummary[]).filter((o) => !o.acknowledged)
          : [],
      }))
      .filter((row) => row.overlaps.length > 0);
    if (pending.length === 0) return;
    const [first, ...rest] = pending;
    setConflictEntry(first.entry);
    setConflictOverlaps(first.overlaps);
    setConflictQueue(rest);
  }, []);

  const maybeWarnOverlaps = useCallback(
    (saved: TimeEntry | null | undefined) => queueOverlapWarnings([saved]),
    [queueOverlapWarnings],
  );

  /** Advance to the next queued conflict, or close when the queue is empty. */
  const closeConflictDialog = useCallback(() => {
    setConflictQueue((queue) => {
      const [next, ...rest] = queue;
      if (next) {
        setConflictEntry(next.entry);
        setConflictOverlaps(next.overlaps);
        return rest;
      }
      setConflictEntry(null);
      setConflictOverlaps([]);
      return [];
    });
  }, []);

  const fetchOverlapPairs = useCallback(async () => {
    try {
      setOverlapLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/time-entries/overlaps`);
      setOverlapPairs(response.data?.pairs ?? []);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load overlapping entries'));
    } finally {
      setOverlapLoading(false);
    }
  }, []);

  useEffect(() => {
    if (overlapPanelOpen) fetchOverlapPairs();
  }, [overlapPanelOpen, fetchOverlapPairs]);

  const toggleOverlapAck = async (pair: OverlapPair) => {
    const key = `${pair.entry_a.id}-${pair.entry_b.id}`;
    try {
      setAckBusyKey(key);
      if (pair.acknowledged) {
        await axios.delete(`${API_BASE_URL}/api/time-entries/overlaps/acknowledge`, {
          params: { entry_a_id: pair.entry_a.id, entry_b_id: pair.entry_b.id },
        });
      } else {
        await axios.post(`${API_BASE_URL}/api/time-entries/overlaps/acknowledge`, {
          entry_a_id: pair.entry_a.id,
          entry_b_id: pair.entry_b.id,
        });
      }
      await fetchOverlapPairs();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to update overlap acknowledgement'));
    } finally {
      setAckBusyKey(null);
    }
  };

  // After saving a closed entry, check (best-effort, per the gaps endpoint)
  // whether an unaccounted gap ends right where the new entry starts — that
  // is the moment the backfill wizard is wanted. The offer is a dismissible
  // alert, never a forced modal.
  const maybeOfferBackfill = useCallback(async (startIso: string) => {
    try {
      const started = new Date(startIso);
      if (Number.isNaN(started.getTime())) return;
      const response = await axios.get(`${API_BASE_URL}/api/time-entries/gaps`, {
        params: {
          date: toLocalDateValue(started),
          tz_offset: started.getTimezoneOffset(),
        },
      });
      const gaps = response.data?.gaps;
      if (!Array.isArray(gaps)) return;
      const behind = (gaps as BackfillGap[]).find(
        (gap) => Math.abs(new Date(gap.end).getTime() - started.getTime()) <= 60000,
      );
      if (behind) setOfferGap(behind);
    } catch {
      // Best-effort: the offer is a nicety, never an error surface.
    }
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = buildPayload();
      let saved: TimeEntry | null = null;
      if (editingEntry) {
        const response = await axios.put<TimeEntry>(
          `${API_BASE_URL}/api/time-entries/${editingEntry.id}`,
          payload,
        );
        saved = response.data;
        setInfo('Time entry updated.');
      } else {
        const response = await axios.post<TimeEntry>(
          `${API_BASE_URL}/api/time-entries/`,
          payload,
        );
        saved = response.data;
        setInfo('Time entry created.');
      }
      setDialogOpen(false);
      await fetchInitial();
      maybeWarnOverlaps(saved);
      if (!editingEntry && payload.end_time) {
        await maybeOfferBackfill(payload.start_time);
      }
    } catch (err: any) {
      setError(
        err instanceof Error && !axios.isAxiosError(err)
          ? err.message
          : getApiErrorMessage(err, 'Failed to save time entry'),
      );
    } finally {
      setSaving(false);
    }
  };

  const openQuick = () => {
    setQuick(createDefaultQuick());
    setQuickOpen(true);
  };

  const buildQuickPayload = () => {
    const base = {
      description: optionalString(quick.description),
      project: optionalString(quick.project),
      work_timer_id: quick.work_timer_id ? Number(quick.work_timer_id) : null,
    };

    if (quick.mode === 'worked') {
      const minutes = parseQuickMinutes(quick.duration_minutes, 'Duration');
      const end = quick.worked_end ? new Date(quick.worked_end) : new Date();
      if (Number.isNaN(end.getTime())) {
        throw new Error('End time is invalid');
      }
      if (end.getTime() > Date.now() + FUTURE_TOLERANCE_MS) {
        throw new Error("End time can't be in the future");
      }
      const start = new Date(end.getTime() - minutes * 60000);
      return {
        ...base,
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        expected_end: null,
      };
    }

    // 'started' and 'continue' both create a running entry (end_time null).
    const start = quick.started_at ? new Date(quick.started_at) : new Date();
    if (Number.isNaN(start.getTime())) {
      throw new Error('Start time is invalid');
    }
    if (start.getTime() > Date.now() + FUTURE_TOLERANCE_MS) {
      throw new Error("Start time can't be in the future");
    }

    if (quick.mode === 'continue') {
      const minutes = parseQuickMinutes(quick.continue_minutes, 'Plan-to-continue minutes');
      // expected_end is "plan N more from now" — a soft target, never actual time.
      const expectedEnd = new Date(Date.now() + minutes * 60000);
      return {
        ...base,
        start_time: start.toISOString(),
        end_time: null,
        expected_end: expectedEnd.toISOString(),
      };
    }

    return {
      ...base,
      start_time: start.toISOString(),
      end_time: null,
      expected_end: null,
    };
  };

  const handleQuickSave = async () => {
    try {
      setQuickSaving(true);
      const payload = buildQuickPayload();
      const response = await axios.post<TimeEntry>(
        `${API_BASE_URL}/api/time-entries/`,
        payload,
      );
      setInfo(
        quick.mode === 'worked'
          ? 'Logged time entry.'
          : quick.mode === 'continue'
            ? 'Started a running entry with a planned stop.'
            : 'Started a running entry.',
      );
      setQuickOpen(false);
      await fetchInitial();
      maybeWarnOverlaps(response.data);
      if (quick.mode === 'worked' && payload.end_time) {
        await maybeOfferBackfill(payload.start_time);
      }
    } catch (err: any) {
      setError(
        err instanceof Error && !axios.isAxiosError(err)
          ? err.message
          : getApiErrorMessage(err, 'Failed to log time'),
      );
    } finally {
      setQuickSaving(false);
    }
  };

  const handleStop = async (entry: TimeEntry) => {
    try {
      setStoppingId(entry.id);
      // Reuse the existing update path: setting end_time materialises
      // duration_seconds server-side, exactly like the Work Timer stop.
      const response = await axios.put<TimeEntry>(
        `${API_BASE_URL}/api/time-entries/${entry.id}`,
        { end_time: new Date().toISOString() },
      );
      setInfo('Stopped running entry.');
      await fetchInitial();
      // Stopping is the moment the entry becomes closed — the response now
      // carries any overlap warnings for it.
      maybeWarnOverlaps(response.data);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to stop entry'));
    } finally {
      setStoppingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleteTarget.source !== 'manual') return;
    try {
      setDeleting(true);
      await axios.delete(`${API_BASE_URL}/api/time-entries/${deleteTarget.id}`);
      setDeleteTarget(null);
      setInfo('Time entry deleted.');
      await fetchInitial();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to delete time entry'));
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await axios.get(`${API_BASE_URL}/api/time-entries/export.csv`, {
        params: filterParams,
        responseType: 'blob',
      });
      triggerBlobDownload(
        response.data,
        response.headers['content-type'] || 'text/csv',
        'time-entries.csv',
      );
      setInfo('CSV exported.');
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to export CSV'));
    } finally {
      setExporting(false);
    }
  };

  const hasMore = summary ? entries.length < summary.entry_count : false;

  const renderSourceChip = (source: TimeEntrySource) => (
    <Chip label={SOURCE_LABELS[source]} size="small" variant="outlined" />
  );

  const renderBillable = (entry: TimeEntry) => {
    if (entry.billable == null) return <Chip label="Unspecified" size="small" />;
    return (
      <Stack spacing={0.5}>
        <Chip
          label={entry.billable ? 'Billable' : 'Non-billable'}
          color={entry.billable ? 'success' : 'default'}
          size="small"
        />
        {entry.billable_rate != null && (
          <Typography variant="caption" color="text.secondary">
            {formatMoney(entry.billable_rate, entry.billable_rate_currency)} / hr
          </Typography>
        )}
      </Stack>
    );
  };

  const renderEntryActions = (entry: TimeEntry) => {
    const isRunning = entry.end_time == null;
    if (entry.source !== 'manual' && !isRunning) {
      return (
        <Typography variant="caption" color="text.secondary">
          Imported
        </Typography>
      );
    }
    return (
      <Stack direction="row" spacing={0.5}>
        {isRunning && (
          <Tooltip title="Stop">
            <span>
              <IconButton
                size="small"
                color="warning"
                disabled={stoppingId === entry.id}
                aria-label={`Stop entry ${entry.id}`}
                onClick={() => handleStop(entry)}
              >
                {stoppingId === entry.id ? (
                  <CircularProgress size={16} />
                ) : (
                  <StopIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
        {entry.source === 'manual' && (
          <>
            <Tooltip title="Edit">
              <IconButton size="small" aria-label={`Edit entry ${entry.id}`} onClick={() => openEdit(entry)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                aria-label={`Delete entry ${entry.id}`}
                onClick={() => setDeleteTarget(entry)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Stack>
    );
  };

  return (
    <PageShell maxWidth="wide">
      <Box sx={pageHeaderSx}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={2}
        >
          <Box>
            <Typography variant="h3" component="h1">
              Time Entries
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Review, report, export, and maintain actual logged work records.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchInitial} aria-label="Refresh time entries">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={exporting ? <CircularProgress size={18} /> : <FileDownloadIcon />}
              onClick={handleExport}
              disabled={exporting}
            >
              Export CSV
            </Button>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>
              New entry
            </Button>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => {
                setWizardGap(null);
                setWizardOpen(true);
              }}
            >
              Backfill
            </Button>
            <Button
              variant="outlined"
              startIcon={<CompareArrowsIcon />}
              onClick={() => setOverlapPanelOpen((prev) => !prev)}
            >
              Overlaps
            </Button>
            <Button variant="contained" startIcon={<TimerIcon />} onClick={openQuick}>
              Log time
            </Button>
          </Stack>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={sectionGapSx} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={sectionGapSx} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}
      {offerGap && (
        <Alert
          severity="info"
          sx={sectionGapSx}
          action={
            <Stack direction="row" spacing={1}>
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setWizardGap(offerGap);
                  setOfferGap(null);
                  setWizardOpen(true);
                }}
              >
                Reconstruct
              </Button>
              <Button color="inherit" size="small" onClick={() => setOfferGap(null)}>
                Dismiss
              </Button>
            </Stack>
          }
        >
          {`${
            offerGap.minutes ??
            Math.max(
              0,
              Math.round(
                (new Date(offerGap.end).getTime() - new Date(offerGap.start).getTime()) /
                  60000,
              ),
            )
          }m unaccounted right before the entry you just saved. Walk it back?`}
        </Alert>
      )}

      <Grid container spacing={pageGridSpacing} sx={sectionGapSx}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={cardContentPaddingSx}>
              <Typography variant="caption" color="text.secondary">
                Total duration
              </Typography>
              <Typography variant="h5">{formatDuration(summary?.total_duration_seconds ?? 0)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={cardContentPaddingSx}>
              <Typography variant="caption" color="text.secondary">
                Billable duration
              </Typography>
              <Typography variant="h5">{formatDuration(summary?.billable_duration_seconds ?? 0)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={cardContentPaddingSx}>
              <Typography variant="caption" color="text.secondary">
                Billable amount
              </Typography>
              <Typography variant="h5">
                {summary
                  ? formatMoney(summary.billable_amount, summary.billable_amount_currency)
                  : formatMoney(0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={cardContentPaddingSx}>
              <Typography variant="caption" color="text.secondary">
                Entries
              </Typography>
              <Typography variant="h5">{summary?.entry_count ?? 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <CollapsibleFilterBar
        panelKey="time_entries_filter_bar"
        activeFilterCount={activeFilterCount}
      >
        <Grid container spacing={pageGridSpacing}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start"
              InputLabelProps={{ shrink: true }}
              value={filters.startDate}
              onChange={(e) => setFilter('startDate', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End"
              InputLabelProps={{ shrink: true }}
              value={filters.endDate}
              onChange={(e) => setFilter('endDate', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="source-filter-label">Source</InputLabel>
              <Select
                labelId="source-filter-label"
                label="Source"
                value={filters.source}
                onChange={(e) => setFilter('source', e.target.value as Filters['source'])}
              >
                <MenuItem value="all">All sources</MenuItem>
                {SOURCE_OPTIONS.map((source) => (
                  <MenuItem key={source} value={source}>
                    {SOURCE_LABELS[source]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="work-timer-filter-label">Work timer</InputLabel>
              <Select
                labelId="work-timer-filter-label"
                label="Work timer"
                value={filters.workTimerId}
                onChange={(e) => setFilter('workTimerId', e.target.value)}
              >
                <MenuItem value="">All timers</MenuItem>
                {workTimers.map((timer) => (
                  <MenuItem key={timer.id} value={String(timer.id)}>
                    {timer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Project"
              value={filters.project}
              onChange={(e) => setFilter('project', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Client"
              value={filters.client}
              onChange={(e) => setFilter('client', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Task"
              value={filters.task}
              onChange={(e) => setFilter('task', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="billable-filter-label">Billable</InputLabel>
              <Select
                labelId="billable-filter-label"
                label="Billable"
                value={filters.billable}
                onChange={(e) => setFilter('billable', e.target.value as Filters['billable'])}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Billable</MenuItem>
                <MenuItem value="false">Non-billable</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={9}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={filters.q}
              onChange={(e) => setFilter('q', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.runningOnly}
                    onChange={(e) => setFilter('runningOnly', e.target.checked)}
                  />
                }
                label="Running only"
              />
              <Button onClick={() => setFilters(emptyFilters)}>Reset</Button>
            </Stack>
          </Grid>
        </Grid>
      </CollapsibleFilterBar>

      {overlapPanelOpen && (
        <Paper sx={[panelPaddingSx, sectionGapSx]}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle1">Overlapping entries</Typography>
            <Button size="small" onClick={fetchOverlapPairs} disabled={overlapLoading}>
              Refresh
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Closed entries whose times overlap. Overlaps are allowed — acknowledge a
            pair to stop it warning on future edits. Imported history was never
            overlap-checked, so expect old pairs here.
          </Typography>
          {overlapLoading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={22} />
            </Box>
          ) : overlapPairs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No overlapping closed entries.
            </Typography>
          ) : (
            <Stack spacing={1} divider={<Divider flexItem />}>
              {overlapPairs.map((pair) => {
                const key = `${pair.entry_a.id}-${pair.entry_b.id}`;
                return (
                  <Stack
                    key={key}
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    gap={1}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2">
                        {`${pair.entry_a.description || 'Untitled entry'} ↔ ${
                          pair.entry_b.description || 'Untitled entry'
                        }`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {`${formatDateTime(pair.entry_a.start_time)} – ${formatDateTime(
                          pair.entry_a.end_time,
                        )} ↔ ${formatDateTime(pair.entry_b.start_time)} – ${formatDateTime(
                          pair.entry_b.end_time,
                        )}`}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={pair.acknowledged ? 'Accepted' : 'Needs review'}
                        color={pair.acknowledged ? 'success' : 'warning'}
                        variant="outlined"
                      />
                      <Button
                        size="small"
                        disabled={ackBusyKey === key}
                        onClick={() => toggleOverlapAck(pair)}
                      >
                        {ackBusyKey === key ? (
                          <CircularProgress size={16} />
                        ) : pair.acknowledged ? (
                          'Un-acknowledge'
                        ) : (
                          'Acknowledge'
                        )}
                      </Button>
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Paper>
      )}

      {summary && summary.entry_count > 0 && (
        <Grid container spacing={pageGridSpacing} sx={sectionGapSx}>
          {(
            [
              ['source', 'Source'],
              ['project', 'Project'],
              ['client', 'Client'],
              ['task', 'Task'],
            ] as const
          ).map(([key, label]) => (
            <Grid item xs={12} md={6} lg={3} key={key}>
              <Paper sx={panelPaddingSx}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {label}
                </Typography>
                <Stack spacing={1}>
                  {summary.breakdowns[key].slice(0, 5).map((item) => (
                    <Box key={`${key}-${item.key || 'none'}`}>
                      <Stack direction="row" justifyContent="space-between" gap={1}>
                        <Typography variant="body2" noWrap title={item.key || 'Unassigned'}>
                          {key === 'source' && item.key
                            ? SOURCE_LABELS[item.key as TimeEntrySource] || item.key
                            : item.key || 'Unassigned'}
                        </Typography>
                        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatDuration(item.duration_seconds)}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {item.entry_count} entr{item.entry_count === 1 ? 'y' : 'ies'}
                        {item.billable_amount != null && item.billable_amount > 0
                          ? ` · ${formatMoney(item.billable_amount, item.billable_amount_currency)}`
                          : ''}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="35vh">
          <CircularProgress />
        </Box>
      ) : entries.length === 0 ? (
        <Paper sx={[panelPaddingSx, { textAlign: 'center' }]}>
          <Typography variant="h6" gutterBottom>
            No time entries match these filters
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New entry
          </Button>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Table size="small" aria-label="Time entries table">
              <TableHead>
                <TableRow>
                  <TableCell sx={tableCellPaddingSx}>When</TableCell>
                  <TableCell sx={tableCellPaddingSx}>Duration</TableCell>
                  <TableCell sx={tableCellPaddingSx}>Description</TableCell>
                  <TableCell sx={tableCellPaddingSx}>Source</TableCell>
                  <TableCell sx={tableCellPaddingSx}>Project</TableCell>
                  <TableCell sx={tableCellPaddingSx}>Client</TableCell>
                  <TableCell sx={tableCellPaddingSx}>Task</TableCell>
                  <TableCell sx={tableCellPaddingSx}>Billable</TableCell>
                  <TableCell sx={tableCellPaddingSx}>Amount</TableCell>
                  <TableCell sx={tableCellPaddingSx}>Source user</TableCell>
                  <TableCell sx={tableCellPaddingSx}>Source created</TableCell>
                  <TableCell sx={tableCellPaddingSx} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => {
                  const timer = entry.work_timer_id ? timerById.get(entry.work_timer_id) : null;
                  const expected = describeExpectedEnd(entry, nowTick);
                  return (
                    <TableRow key={entry.id} hover>
                      <TableCell sx={tableCellPaddingSx}>
                        <Typography variant="body2">{formatDateTime(entry.start_time)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(entry.end_time)}
                        </Typography>
                        {entry.end_time == null && (
                          <Box sx={{ mt: 0.5 }}>
                            <Chip label="Running" color="warning" size="small" />
                            {expected && (
                              <Typography
                                variant="caption"
                                display="block"
                                color={expected.over ? 'warning.main' : 'text.secondary'}
                              >
                                {expected.text}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={tableCellPaddingSx}>
                        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatDuration(entry.duration_seconds)}
                        </Typography>
                        {entry.duration_decimal != null && (
                          <Typography variant="caption" color="text.secondary">
                            {entry.duration_decimal}h decimal
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={tableCellPaddingSx}>
                        <Typography variant="body2">{entry.description || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {entry.tags || ''}
                        </Typography>
                      </TableCell>
                      <TableCell sx={tableCellPaddingSx}>{renderSourceChip(entry.source)}</TableCell>
                      <TableCell sx={tableCellPaddingSx}>
                        <Typography variant="body2">{entry.project || '-'}</Typography>
                        {timer && (
                          <Typography variant="caption" color="text.secondary">
                            {timer.name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={tableCellPaddingSx}>{entry.client || '-'}</TableCell>
                      <TableCell sx={tableCellPaddingSx}>{entry.task || '-'}</TableCell>
                      <TableCell sx={tableCellPaddingSx}>{renderBillable(entry)}</TableCell>
                      <TableCell sx={tableCellPaddingSx}>
                        {entry.billable_amount == null
                          ? '-'
                          : formatMoney(entry.billable_amount, entry.billable_amount_currency)}
                      </TableCell>
                      <TableCell sx={tableCellPaddingSx}>
                        <Typography variant="body2">{entry.source_user_name || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[entry.source_user_group, entry.source_user_email].filter(Boolean).join(' · ')}
                        </Typography>
                      </TableCell>
                      <TableCell sx={tableCellPaddingSx}>{formatDateTime(entry.source_created_at)}</TableCell>
                      <TableCell sx={tableCellPaddingSx} align="right">
                        {renderEntryActions(entry)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack spacing={1.5} sx={{ display: { xs: 'flex', md: 'none' } }}>
            {entries.map((entry) => {
              const expected = describeExpectedEnd(entry, nowTick);
              return (
              <Paper key={entry.id} sx={panelPaddingSx}>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" gap={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" noWrap>
                        {entry.description || 'Untitled entry'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(entry.start_time)} · {formatDuration(entry.duration_seconds)}
                      </Typography>
                    </Box>
                    {renderEntryActions(entry)}
                  </Stack>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                    {renderSourceChip(entry.source)}
                    {entry.billable === true && <Chip label="Billable" color="success" size="small" />}
                    {entry.billable === false && <Chip label="Non-billable" size="small" />}
                    {entry.end_time == null && <Chip label="Running" color="warning" size="small" />}
                  </Stack>
                  {expected && (
                    <Typography
                      variant="caption"
                      color={expected.over ? 'warning.main' : 'text.secondary'}
                    >
                      {expected.text}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    {[entry.project, entry.client, entry.task].filter(Boolean).join(' · ') || '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[entry.tags, entry.source_user_name, entry.source_user_email].filter(Boolean).join(' · ')}
                  </Typography>
                  {entry.billable_amount != null && (
                    <Typography variant="body2">
                      {formatMoney(entry.billable_amount, entry.billable_amount_currency)}
                    </Typography>
                  )}
                </Stack>
              </Paper>
              );
            })}
          </Stack>

          {hasMore && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Button onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? <CircularProgress size={20} /> : 'Load more'}
              </Button>
            </Box>
          )}
        </>
      )}

      <Dialog
        open={quickOpen}
        onClose={() => (quickSaving ? undefined : setQuickOpen(false))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Log time</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={quick.mode}
              onChange={(_, value) => value && setQuick((prev) => ({ ...prev, mode: value as QuickMode }))}
              aria-label="Log mode"
            >
              <ToggleButton value="worked">I worked for…</ToggleButton>
              <ToggleButton value="started">Started at…</ToggleButton>
              <ToggleButton value="continue">Plan to continue</ToggleButton>
            </ToggleButtonGroup>

            <TextField
              fullWidth
              label="What did you work on?"
              value={quick.description}
              onChange={(e) => setQuick((prev) => ({ ...prev, description: e.target.value }))}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Project (optional)"
                  value={quick.project}
                  onChange={(e) => setQuick((prev) => ({ ...prev, project: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="quick-work-timer-label">Work timer (optional)</InputLabel>
                  <Select
                    labelId="quick-work-timer-label"
                    label="Work timer (optional)"
                    value={quick.work_timer_id}
                    onChange={(e) => setQuick((prev) => ({ ...prev, work_timer_id: e.target.value }))}
                  >
                    <MenuItem value="">None</MenuItem>
                    {workTimers.map((timer) => (
                      <MenuItem key={timer.id} value={String(timer.id)}>
                        {timer.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {quick.mode === 'worked' && (
              <>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Duration
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    sx={{ display: 'flex', flexWrap: 'wrap', mt: 0.5 }}
                    value={
                      DURATION_CHIPS.includes(Number(quick.duration_minutes))
                        ? Number(quick.duration_minutes)
                        : null
                    }
                    onChange={(_, value) =>
                      value != null && setQuick((prev) => ({ ...prev, duration_minutes: String(value) }))
                    }
                    aria-label="Duration preset"
                  >
                    {DURATION_CHIPS.map((minutes) => (
                      <ToggleButton key={minutes} value={minutes}>
                        {minutes}m
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Minutes"
                      inputProps={{ min: 1 }}
                      value={quick.duration_minutes}
                      onChange={(e) => setQuick((prev) => ({ ...prev, duration_minutes: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label="Ended at"
                      InputLabelProps={{ shrink: true }}
                      value={quick.worked_end}
                      onChange={(e) => setQuick((prev) => ({ ...prev, worked_end: e.target.value }))}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            {(quick.mode === 'started' || quick.mode === 'continue') && (
              <>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Started at"
                  InputLabelProps={{ shrink: true }}
                  helperText="Creates a running entry — stop it later from the table."
                  value={quick.started_at}
                  onChange={(e) => setQuick((prev) => ({ ...prev, started_at: e.target.value }))}
                />
                {hasRunningEntry && (
                  <Alert severity="info">
                    You already have a running entry. Starting another will run in parallel.
                  </Alert>
                )}
              </>
            )}

            {quick.mode === 'continue' && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Plan to work more
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  sx={{ display: 'flex', flexWrap: 'wrap', mt: 0.5 }}
                  value={
                    DURATION_CHIPS.includes(Number(quick.continue_minutes))
                      ? Number(quick.continue_minutes)
                      : null
                  }
                  onChange={(_, value) =>
                    value != null && setQuick((prev) => ({ ...prev, continue_minutes: String(value) }))
                  }
                  aria-label="Plan-to-continue preset"
                >
                  {DURATION_CHIPS.map((minutes) => (
                    <ToggleButton key={minutes} value={minutes}>
                      {minutes}m
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <TextField
                  fullWidth
                  type="number"
                  label="More minutes"
                  sx={{ mt: 1 }}
                  inputProps={{ min: 1 }}
                  helperText="Sets a soft expected-stop target; it never auto-stops the entry."
                  value={quick.continue_minutes}
                  onChange={(e) => setQuick((prev) => ({ ...prev, continue_minutes: e.target.value }))}
                />
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuickOpen(false)} disabled={quickSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleQuickSave} disabled={quickSaving}>
            {quickSaving ? <CircularProgress size={20} /> : quick.mode === 'worked' ? 'Log' : 'Start'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={() => (saving ? undefined : setDialogOpen(false))} fullWidth maxWidth="md">
        <DialogTitle>{editingEntry ? 'Edit time entry' : 'New time entry'}</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          {editingEntry && editingEntry.source !== 'manual' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Imported entries are read-only in this view.
            </Alert>
          )}
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Description"
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="entry-work-timer-label">Work timer</InputLabel>
                <Select
                  labelId="entry-work-timer-label"
                  label="Work timer"
                  value={form.work_timer_id}
                  onChange={(e) => setForm((prev) => ({ ...prev, work_timer_id: e.target.value }))}
                >
                  <MenuItem value="">None</MenuItem>
                  {workTimers.map((timer) => (
                    <MenuItem key={timer.id} value={String(timer.id)}>
                      {timer.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Project"
                value={form.project}
                onChange={(e) => setForm((prev) => ({ ...prev, project: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Client"
                value={form.client}
                onChange={(e) => setForm((prev) => ({ ...prev, client: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Task"
                value={form.task}
                onChange={(e) => setForm((prev) => ({ ...prev, task: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tags"
                value={form.tags}
                onChange={(e) => setForm((prev) => ({ ...prev, tags: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="datetime-local"
                label="Start time"
                InputLabelProps={{ shrink: true }}
                value={form.start_time}
                onChange={(e) => setForm((prev) => ({ ...prev, start_time: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required={!editingEntry}
                type="datetime-local"
                label="End time"
                InputLabelProps={{ shrink: true }}
                value={form.end_time}
                onChange={(e) => setForm((prev) => ({ ...prev, end_time: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Expected stop"
                InputLabelProps={{ shrink: true }}
                helperText="Optional soft target for a running entry — never auto-stops."
                value={form.expected_end}
                onChange={(e) => setForm((prev) => ({ ...prev, expected_end: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="entry-billable-label">Billable</InputLabel>
                <Select
                  labelId="entry-billable-label"
                  label="Billable"
                  value={form.billable}
                  onChange={(e) => setForm((prev) => ({ ...prev, billable: e.target.value as EntryFormState['billable'] }))}
                >
                  <MenuItem value="unset">Unspecified</MenuItem>
                  <MenuItem value="true">Billable</MenuItem>
                  <MenuItem value="false">Non-billable</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Rate"
                value={form.billable_rate}
                onChange={(e) => setForm((prev) => ({ ...prev, billable_rate: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Rate currency"
                inputProps={{ maxLength: 16 }}
                value={form.billable_rate_currency}
                onChange={(e) => setForm((prev) => ({ ...prev, billable_rate_currency: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Amount"
                value={form.billable_amount}
                onChange={(e) => setForm((prev) => ({ ...prev, billable_amount: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Amount currency"
                inputProps={{ maxLength: 16 }}
                value={form.billable_amount_currency}
                onChange={(e) => setForm((prev) => ({ ...prev, billable_amount_currency: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Duration decimal"
                value={form.duration_decimal}
                onChange={(e) => setForm((prev) => ({ ...prev, duration_decimal: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Source created"
                InputLabelProps={{ shrink: true }}
                value={form.source_created_at}
                onChange={(e) => setForm((prev) => ({ ...prev, source_created_at: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Source user"
                value={form.source_user_name}
                onChange={(e) => setForm((prev) => ({ ...prev, source_user_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Source group"
                value={form.source_user_group}
                onChange={(e) => setForm((prev) => ({ ...prev, source_user_group: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Source email"
                value={form.source_user_email}
                onChange={(e) => setForm((prev) => ({ ...prev, source_user_email: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : editingEntry ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <OverlapConflictDialog
        open={Boolean(conflictEntry)}
        entry={conflictEntry}
        overlaps={conflictOverlaps}
        onClose={closeConflictDialog}
        onResolved={() => {
          fetchInitial();
          if (overlapPanelOpen) fetchOverlapPairs();
        }}
      />

      <BackfillWizard
        open={wizardOpen}
        initialGap={wizardGap}
        onClose={() => setWizardOpen(false)}
        onCommitted={(count, created) => {
          setInfo(
            count > 0
              ? `Backfilled ${count} ${count === 1 ? 'entry' : 'entries'}.`
              : 'This backfill batch was already saved.',
          );
          fetchInitial();
          // Dragging can now propose time that crosses an existing entry, so a
          // batch can land overlaps. Warn through the same dialog every other
          // write path uses — never a second conflict UI.
          if (created?.length) queueOverlapWarnings(created as unknown as TimeEntry[]);
        }}
      />

      <Dialog open={Boolean(deleteTarget)} onClose={() => (deleting ? undefined : setDeleteTarget(null))}>
        <DialogTitle>Delete time entry</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <DialogContentText>
            Delete this manual time entry? Imported Clockify and Google rows are not affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default TimeEntries;
